<template>
  <!-- Application mobile des patients : la clinique ouvre le dossier à un numéro vérifié avec la personne -->
  <div class="border rounded p-3 bg-white">
    <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
      <div>
        <h6 class="fw-bold text-primary mb-0"><i class="bi bi-phone me-1"></i>Application mobile</h6>
        <small class="text-muted">Le patient (ou son parent) consulte ses rendez-vous, résultats validés, ordonnances et paiements, et prend rendez-vous.</small>
      </div>
      <button v-if="peutModifier && !formulaire" type="button" class="btn btn-sm btn-outline-primary" @click="ouvrirFormulaire()">
        <i class="bi bi-plus-lg me-1"></i>Ouvrir l'accès
      </button>
    </div>

    <div v-if="chargement" class="text-muted small"><span class="spinner-border spinner-border-sm me-1"></span>Chargement…</div>

    <template v-else>
      <div v-if="!actifs.length && !formulaire" class="text-muted fst-italic small">Aucun accès ouvert.</div>

      <ul v-if="actifs.length" class="list-group list-group-flush mb-2">
        <li v-for="a in actifs" :key="a.id" class="list-group-item px-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div class="small">
            <strong>{{ a.telephone }}</strong> · {{ a.lienLibelle }}
            <div class="text-muted">
              Ouvert le {{ date(a.ouvertLe) }}<span v-if="a.ouvertPar"> par {{ a.ouvertPar }}</span>
              · <span v-if="a.derniereConnexion">dernière connexion le {{ date(a.derniereConnexion) }}</span><span v-else>jamais connecté</span>
            </div>
          </div>
          <button v-if="peutModifier" type="button" class="btn btn-sm btn-link text-danger p-0" :disabled="enCours" @click="retirer(a)">
            <i class="bi bi-x-circle me-1"></i>Retirer l'accès
          </button>
        </li>
      </ul>

      <div v-if="formulaire" class="border rounded p-3 bg-light">
        <div class="alert alert-warning py-2 small mb-2">
          <i class="bi bi-shield-exclamation me-1"></i>Vérifiez le numéro <strong>avec la personne présente</strong> :
          celui qui reçoit le code SMS verra ce dossier.
        </div>
        <div v-if="suggestions.length" class="mb-2 small">
          Numéros du dossier :
          <button v-for="s in suggestions" :key="s.telephone" type="button" class="btn btn-sm btn-outline-secondary ms-1 mb-1" @click="choisir(s)">
            {{ s.telephone }} <span class="text-muted">({{ s.libelle }})</span>
          </button>
        </div>
        <div class="row g-2 align-items-end">
          <div class="col-md-5">
            <label class="form-label small mb-1">Téléphone *</label>
            <input type="tel" class="form-control form-control-sm" v-model.trim="telephone" placeholder="+223 76 00 00 00">
          </div>
          <div class="col-md-4">
            <label class="form-label small mb-1">Qui utilisera l'application ?</label>
            <select class="form-select form-select-sm" v-model="lien">
              <option value="TITULAIRE">Le patient lui-même</option>
              <option value="RESPONSABLE">Un parent ou tuteur</option>
            </select>
          </div>
          <div class="col-md-3">
            <div class="form-check">
              <input id="sms-invitation" class="form-check-input" type="checkbox" v-model="envoyerSms">
              <label class="form-check-label small" for="sms-invitation">Prévenir par SMS</label>
            </div>
          </div>
        </div>
        <div v-if="erreur" class="alert alert-danger py-2 small mt-2 mb-0">{{ erreur }}</div>
        <div class="d-flex justify-content-end gap-2 mt-2">
          <button type="button" class="btn btn-sm btn-light border" @click="formulaire = false">Annuler</button>
          <button type="button" class="btn btn-sm btn-success" :disabled="enCours || !telephone" @click="ouvrir">
            <span v-if="enCours" class="spinner-border spinner-border-sm me-1"></span>Ouvrir l'accès
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import api, { messageErreur, notifier } from '@/api'

export default {
  name: 'ApplicationMobilePatient',
  props: {
    patientId: { type: [Number, String], required: true },
  },
  data() {
    return { acces: [], suggestions: [], chargement: true, formulaire: false, telephone: '', lien: 'TITULAIRE', envoyerSms: true, enCours: false, erreur: '' }
  },
  computed: {
    actifs() { return this.acces.filter(a => a.actif) },
    peutModifier() { return this.$store.getters.hasAnyPermission(['PATIENT_EDIT', 'PATIENT_CREATE']) },
  },
  created() { this.charger() },
  methods: {
    async charger() {
      this.chargement = true
      try {
        const r = await api.custom(`patients/${this.patientId}/application-mobile`, 'GET')
        this.appliquer(r.data)
      } catch (e) {
        this.acces = []
      } finally {
        this.chargement = false
      }
    },
    appliquer(d) {
      this.acces = d.acces || []
      this.suggestions = d.suggestions || []
    },
    ouvrirFormulaire() {
      this.erreur = ''
      const s = this.suggestions[0]
      this.telephone = s ? s.telephone : ''
      this.lien = s ? s.lien : 'TITULAIRE'
      this.formulaire = true
    },
    choisir(s) {
      this.telephone = s.telephone
      this.lien = s.lien
    },
    async ouvrir() {
      this.erreur = ''
      this.enCours = true
      try {
        const r = await api.custom(`patients/${this.patientId}/application-mobile`, 'POST', { telephone: this.telephone, lien: this.lien, envoyerSms: this.envoyerSms })
        this.appliquer(r.data)
        this.formulaire = false
        notifier("Accès ouvert : le patient peut se connecter à l'application avec ce numéro.", 'success')
      } catch (e) {
        this.erreur = messageErreur(e, "L'accès n'a pas pu être ouvert.")
      } finally {
        this.enCours = false
      }
    },
    async retirer(a) {
      if (!window.confirm(`Retirer l'accès du ${a.telephone} à ce dossier ?`)) return
      this.enCours = true
      try {
        const r = await api.custom(`application-mobile/${a.id}/retirer`, 'POST')
        this.appliquer(r.data)
        notifier('Accès retiré.', 'success')
      } catch (e) {
        notifier(messageErreur(e, "L'accès n'a pas pu être retiré."))
      } finally {
        this.enCours = false
      }
    },
    date(v) {
      if (!v) return ''
      const [j, h] = String(v).split(' ')
      return j.split('-').reverse().join('/') + (h ? ' à ' + h : '')
    },
  },
}
</script>
